'use client';

import { useState, useEffect, useMemo } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { useAuthStore } from '@/stores/auth';
import { useParams, useSearchParams } from 'next/navigation';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import NextJsImage from "@/components/NextJsImage";
import { useRouter, Link } from '@/i18n/routing';
import CTAForm from '@/components/contact/CTAForm';
import UniqueVisitsIndicator from '@/components/UniqueVisitsIndicator';
import { formatLocation } from '@/lib/location';
import MarkdownView from '@/components/MarkdownView';
import { useDashboardMode } from '@/context/DashboardContext';
import { recalculateCruise } from '@/actions/admin';
import { NotFoundPage } from '@/components/NotFoundPage';
import ShareCruiseSection from '@/components/cruises/ShareCruiseSection';

// Simple Spinner Component
const Spinner = ({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) => {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-8 w-8',
    lg: 'h-12 w-12',
  };
  return (
    <div className={`animate-spin rounded-full border-b-2 border-blue-600 ${sizeClasses[size]}`}></div>
  );
};

const ReadOnlyMapComponent = dynamic(() => import('@/components/ReadOnlyMapComponent'), {
  ssr: false,
  loading: () => (
    <div className="h-full flex items-center justify-center bg-slate-50 dark:bg-slate-800/50">
      <Spinner />
    </div>
  ),
});

interface CruisePlace {
  id?: string;
  name?: string;
  place_name?: string;
  coordinates?: [number, number];
  center?: [number, number];
  latitude?: number;
  longitude?: number;
  country?: string;
  country_code?: string;
}

interface Cruise {
  id: string;
  main_cruise_id?: string | null;
  slug?: string | null;
  name?: string;
  description?: string | null;
  state?: string;
  embarkation_place?: CruisePlace | null;
  disembarkation_place?: CruisePlace | null;
  embarkation_date?: string | null;
  disembarkation_date?: string | null;
  hero_image_url?: string | null;
  images?: Array<{ id?: string; url?: string; image_type?: string; order?: number }>;
  price?: number | null;
  currency?: string | null;
  cruise_time_days?: number | null;
  route_geojson?: string | null;
  route_config?: string | null;
  middle_break_ports?: CruisePlace[] | null;
  route_waypoints?: Array<{ lat: number; lng: number }>;
  route?: Array<[number, number]> | { waypoints?: Array<{ lat: number; lng: number }> }; // Legacy [lng, lat] or new { waypoints }
  route_length_nm?: number | null;
  plannedDistance?: number | null;
  planned_distance?: number | null;
  total_spots?: number | null;
  cruise_type?: string | null;
  created_at?: string;
  updated_at?: string;
  user_id?: string;
  organization_id?: string;
  organization_name?: string | null;
  organization_slug?: string | null;
  organization_website?: string | null;
  organization_logo_url?: string | null;
  owner_id?: string;
  equipment?: {
    webasto?: boolean;
    spinnaker?: boolean;
    solar_panels?: boolean;
    dinghy_engine?: boolean;
    wifi?: boolean;
    air_conditioning?: boolean;
    [key: string]: unknown;
  } | null;
  /** Region names in the request language (legacy); empty if not preloaded or lang empty. */
  region_names?: string[];
  /** Regions with name and slug for linking to region pages. New format from API. */
  regions?: Array<{ name: string; slug: string }>;
  /** Route/regions calculation status from workflow API: e.g. 'processing' | 'ready' */
  route_status?: string;
  unique_visits?: number | null;
}

// UUID regex pattern (unused, kept for potential future use)
// const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Helper function to format dynamic cruise title
function toCityOnlyLabel(place: string | undefined): string | undefined {
  if (!place) return undefined;
  const city = place.split(',')[0]?.trim();
  return city || place;
}

function formatCruiseTitle(
  name: string | undefined,
  embarkationName: string | undefined,
  disembarkationName: string | undefined,
  middlePortNames: string[] = []
): string {
  const baseName = name || 'Cruise';
  const routeNames = [embarkationName, ...middlePortNames, disembarkationName]
    .map((part) => toCityOnlyLabel(part))
    .filter(
    (part): part is string => Boolean(part && part.trim())
  );
  if (routeNames.length >= 2) {
    return `${baseName} | ${routeNames.join(' → ')}`;
  }
  return baseName;
}

interface CruiseByIdClientProps {
  initialCruise?: Cruise | null;
}

export default function CruiseByIdClient({ initialCruise = null }: CruiseByIdClientProps) {
  const t = useTranslations();
  const locale = useLocale();
  const authStore = useAuthStore();
  const router = useRouter();
  const params = useParams();
  const searchParams = useSearchParams();
  const cruiseId = params.id as string;

  const [cruise, setCruise] = useState<Cruise | null>(initialCruise);
  const [loading, setLoading] = useState(!initialCruise);
  const [error, setError] = useState<string | null>(null);
  const [canManage, setCanManage] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [recalculateLoading, setRecalculateLoading] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  const { isAdminContext } = useDashboardMode();
  const isMasterAdmin = !!authStore.user?.is_master_admin;
  const showRecalculateButton = isMasterAdmin && isAdminContext;

  const sortedImages = useMemo(() => {
    return cruise?.images?.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)) || [];
  }, [cruise?.images]);

  const slides = useMemo(() => sortedImages.map((image) => ({ src: image.url || '' })), [sortedImages]);

  const middleBreakPortsForTitle = useMemo(
    () =>
      ((cruise?.middle_break_ports ?? [])
        .map((p) => p?.place_name || p?.name || '')
        .filter(Boolean)),
    [cruise?.middle_break_ports]
  );

  const fullTitle = cruise
    ? formatCruiseTitle(
        cruise.name,
        cruise.embarkation_place?.name,
        cruise.disembarkation_place?.name,
        middleBreakPortsForTitle
      )
    : '';

  useEffect(() => {
    // Validate that we have a cruiseId (can be UUID or slug)
    if (!cruiseId) {
      setError(t('cruiseView.notFound') || 'Cruise not found');
      setLoading(false);
      return;
    }

    // If we have initial cruise data from SSR, use it and only fetch if needed for workflow/preview
    if (initialCruise) {
      setCruise(initialCruise);
      setCanManage(userCanManageCruise(initialCruise));
      setLoading(false);
      
      // Only fetch if user is logged in and might need workflow version
      if (!authStore.authLoading && authStore.isLoggedIn) {
        // Check if we need to fetch workflow version (for drafts/previews)
        fetchCruise();
      }
      return;
    }

    // Wait for auth to finish loading before fetching cruise
    if (authStore.authLoading) {
      return;
    }

    fetchCruise();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cruiseId, authStore.authLoading, initialCruise]);

  const userCanManageCruise = (cruiseData: Cruise | null): boolean => {
    if (!cruiseData || !authStore.user) {
      return false;
    }

    const isOwner = !!cruiseData.owner_id && cruiseData.owner_id === authStore.user.id;
    const isUserOwner = !!cruiseData.user_id && cruiseData.user_id === authStore.user.id;
    const isOrgMember =
      !!cruiseData.organization_id &&
      Boolean(
        authStore.user.memberships?.some(
          (membership) => membership.organization.id === cruiseData.organization_id
        )
      );
    const isMasterAdmin = !!authStore.user.is_master_admin;

    return isOwner || isUserOwner || isOrgMember || isMasterAdmin;
  };

  const fetchCruise = async () => {
    setLoading(true);
    setError(null);

    try {
      // Check if preview parameter is set (for draft/ready_to_publish cruises from dashboard)
      const isPreview = searchParams.get('preview') === 'true';
      
      // If preview mode and user is logged in, use workflow endpoint directly
      if (isPreview && !authStore.authLoading && authStore.isLoggedIn) {
        // For draft/preview cruises, workflow endpoint requires main_cruise_id (UUID format)
        // First, try to get the cruise to find main_cruise_id if cruiseId is not already a UUID
        let workflowCruiseId = cruiseId;
        
        // Check if cruiseId looks like a UUID (contains hyphens) or is a slug
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(cruiseId);
        
        if (!isUUID) {
          // cruiseId might be a slug or version id, try to get main_cruise_id
          let tempResponse = await fetch(`/api/cruises/slug/${cruiseId}`, {
            credentials: 'include',
          });
          if (tempResponse.status === 404) {
            tempResponse = await fetch(`/api/cruises/${cruiseId}`, {
              credentials: 'include',
            });
          }
          if (tempResponse.ok) {
            const tempData = await tempResponse.json();
            // Use main_cruise_id (UUID) for workflow endpoint
            if (tempData.main_cruise_id) {
              workflowCruiseId = tempData.main_cruise_id;
            }
          }
        }
        
        // Use workflow endpoint with main_cruise_id (UUID format)
        const workflowResponse = await authStore.authorizedFetch(`/api/cruises/${workflowCruiseId}/workflow`, {
          method: 'GET',
        });
        
        if (!workflowResponse.ok) {
          if (workflowResponse.status === 403) {
            throw new Error(t('cruiseView.restricted') || 'You do not have access to this cruise');
          }
          if (workflowResponse.status === 404) {
            throw new Error(t('cruiseView.notFound') || 'Cruise not found');
          }
          const errorData = await workflowResponse.json().catch(() => ({}));
          throw new Error(errorData.detail || errorData.message || `Failed to load cruise (${workflowResponse.status})`);
        }
        
        const data = await workflowResponse.json();
        setCruise(data);
        setCanManage(userCanManageCruise(data));
        return;
      }

      // For published cruises, try regular endpoints first
      // First: Try slug endpoint, then fallback to ID endpoint
      let response = await fetch(`/api/cruises/slug/${cruiseId}`, {
        credentials: 'include',
      });

      // If slug endpoint fails (404), try ID endpoint as fallback
      if (response.status === 404) {
        response = await fetch(`/api/cruises/${cruiseId}`, {
          credentials: 'include',
        });
      }

      // If published cruise not found (404/403) and user is authenticated, try workflow endpoint for preview/draft version
      if ((response.status === 404 || response.status === 403) && !authStore.authLoading && authStore.isLoggedIn) {
        // First try with cruiseId (could be version id or main_cruise_id)
        let workflowResponse = await authStore.authorizedFetch(`/api/cruises/${cruiseId}/workflow`, {
          method: 'GET',
        });

        // If that fails, try to get the cruise first to find main_cruise_id
        if ((workflowResponse.status === 404 || workflowResponse.status === 403)) {
          // Try slug endpoint first, then ID endpoint
          let tempResponse = await fetch(`/api/cruises/slug/${cruiseId}`, {
            credentials: 'include',
          });
          if (tempResponse.status === 404) {
            tempResponse = await fetch(`/api/cruises/${cruiseId}`, {
              credentials: 'include',
            });
          }
          if (tempResponse.ok) {
            const tempData = await tempResponse.json();
            // If we have main_cruise_id, use it for workflow endpoint
            if (tempData.main_cruise_id && tempData.main_cruise_id !== cruiseId) {
              workflowResponse = await authStore.authorizedFetch(`/api/cruises/${tempData.main_cruise_id}/workflow`, {
                method: 'GET',
              });
            }
          }
        }
        response = workflowResponse;
      }

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(t('cruiseView.notFound') || 'Cruise not found');
        }
        if (response.status === 403) {
          throw new Error(t('cruiseView.restricted') || 'You do not have access to this cruise');
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.message || `Failed to load cruise (${response.status})`);
      }

      const data = await response.json();
      setCruise(data);
      setCanManage(userCanManageCruise(data));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load cruise');
      setCruise(null);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(locale === 'pl' ? 'pl-PL' : 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  /** Format date range smartly: "10-17 February 2026" | "25 Feb - 02 Mar 2026" | "25 Dec 2025 - 02 Jan 2026" */
  const formatDateRange = (startStr: string | null | undefined, endStr: string | null | undefined): string => {
    if (!startStr && !endStr) return '-';
    if (!startStr) return endStr ? formatDate(endStr) : '-';
    if (!endStr) return formatDate(startStr);
    try {
      const start = new Date(startStr);
      const end = new Date(endStr);
      const loc = locale === 'pl' ? 'pl-PL' : 'en-GB';
      const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
      const sameYear = start.getFullYear() === end.getFullYear();
      if (sameMonth) {
        return start.toLocaleDateString(loc, { day: 'numeric' }) + '-' +
          end.toLocaleDateString(loc, { day: 'numeric', month: 'long', year: 'numeric' });
      }
      if (sameYear) {
        return start.toLocaleDateString(loc, { day: 'numeric', month: 'short' }) + ' - ' +
          end.toLocaleDateString(loc, { day: 'numeric', month: 'short', year: 'numeric' });
      }
      return start.toLocaleDateString(loc, { day: 'numeric', month: 'short', year: 'numeric' }) + ' - ' +
        end.toLocaleDateString(loc, { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return `${formatDate(startStr)} - ${formatDate(endStr)}`;
    }
  };

  const formatPlace = (place: CruisePlace | null | undefined): string =>
    formatLocation(place) || '-';

  const handleEdit = () => {
    if (cruise) {
      const editId = cruise.main_cruise_id || cruise.id;
      window.location.href = `/${locale}/cruises/create?id=${editId}`;
    }
  };

  const handleUnpublishOrDelete = () => {
    setDeleteDialogOpen(true);
  };

  const handleConfirmUnpublishOrDelete = async () => {
    if (!cruise) return;

    setDeleting(true);
    try {
      const cruiseIdToUse = cruise.main_cruise_id || cruise.id;

      if (cruise.state === 'published') {
        // Unpublish: change state to draft
        const response = await authStore.authorizedFetch(`/api/cruises/${cruiseIdToUse}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            state: 'draft',
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || errorData.message || t('dashboardPage.unpublishCruise.failed') || 'Failed to unpublish cruise');
        }

        // Update local state
        setCruise({ ...cruise, state: 'draft' });
      } else {
        // Delete: remove the cruise
        const response = await authStore.authorizedFetch(`/api/cruises/${cruiseIdToUse}`, {
          method: 'DELETE',
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || errorData.message || 'Failed to delete cruise');
        }

        // Redirect to cruises list
        router.push('/cruises');
        return;
      }

      setDeleteDialogOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : (cruise.state === 'published' ? (t('dashboardPage.unpublishCruise.failed') || 'Failed to unpublish cruise') : 'Failed to delete cruise'));
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelUnpublishOrDelete = () => {
    setDeleteDialogOpen(false);
  };

  const handleRecalculate = async () => {
    if (!cruise) return;
    setRecalculateLoading(true);
    setError(null);
    try {
      const payload = cruise.slug
        ? { slug: cruise.slug }
        : { main_cruise_id: cruise.main_cruise_id || cruise.id };
      const { error: err } = await recalculateCruise(payload);
      if (err) {
        setError(err);
        return;
      }
      await fetchCruise();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to recalculate cruise');
    } finally {
      setRecalculateLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      </div>
    );
  }

  // Use shared 404 page for all 4XX errors (400–499)
  const is4xxError =
    error &&
    (/\(4\d{2}\)/.test(error) ||
      error.includes(t('cruiseView.notFound') || 'Cruise not found') ||
      error.includes(t('cruiseView.restricted') || 'You do not have access'));
  if (error && !cruise && is4xxError) {
    return <NotFoundPage />;
  }

  if (error || !cruise) {
    return (
      <div className="w-full max-w-7xl mx-auto px-4 py-8">
        <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm">
          <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
            {error || t('cruiseView.notFound') || 'Cruise not found'}
          </div>
          <Link 
            href="/cruises" 
            className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors"
          >
            {t('cruisesPage.title') || 'Cruises'}
          </Link>
        </div>
      </div>
    );
  }

  // Get route config - prefer route_config, fallback to route_geojson
  const routeConfig = cruise.route_config || cruise.route_geojson;
  const plannedDistance = cruise.plannedDistance || cruise.planned_distance || cruise.route_length_nm;
  const middleBreakPorts = (() => {
    const fromPayload = (cruise.middle_break_ports ?? [])
      .filter((p): p is CruisePlace => Boolean(p && (p.place_name || p.name)));
    if (fromPayload.length > 0) return fromPayload;
    if (!routeConfig) return [];
    try {
      const parsed = JSON.parse(routeConfig) as { middleBreakPorts?: unknown };
      if (Array.isArray(parsed.middleBreakPorts)) {
        return parsed.middleBreakPorts
          .map((p) => {
            if (typeof p === 'string') {
              return { id: '', name: p, place_name: p } as CruisePlace;
            }
            if (typeof p === 'object' && p !== null) {
              const place = p as CruisePlace;
              return {
                id: place.id || '',
                name: place.name || place.place_name || '',
                place_name: place.place_name || place.name || '',
                coordinates: place.coordinates,
                center: place.center,
                latitude: place.latitude,
                longitude: place.longitude,
                country: place.country,
                country_code: place.country_code,
              } as CruisePlace;
            }
            return null;
          })
          .filter((p): p is CruisePlace => Boolean(p && (p.place_name || p.name)));
      }
    } catch {
      // ignore
    }
    return [];
  })().map((port) => ({
    id: port.id || '',
    name: port.name || port.place_name || '',
    place_name: port.place_name || port.name || '',
    coordinates: port.coordinates,
    center: port.center,
    latitude: port.latitude,
    longitude: port.longitude,
    country: port.country,
    country_code: port.country_code,
  }));

  const hasWaypoints = 
    (cruise.route_waypoints && cruise.route_waypoints.length > 0) ||
    (Array.isArray(cruise.route) && cruise.route.length > 0) ||
    (!Array.isArray(cruise.route) && cruise.route?.waypoints && cruise.route.waypoints.length > 0);

  let finalRouteGeoJSON = routeConfig;
  // Only fallback to radius mode if we have NO route data (config or waypoints) but have a distance
  if (!finalRouteGeoJSON && !hasWaypoints && plannedDistance) {
    finalRouteGeoJSON = JSON.stringify({
      mode: 'radius',
      radiusNM: plannedDistance,
    });
  }

  return (
    <div className="w-full max-w-7xl mx-auto px-4 py-8">
      <Lightbox
        open={lightboxIndex >= 0}
        close={() => setLightboxIndex(-1)}
        index={lightboxIndex}
        slides={slides}
        render={{ slide: NextJsImage }}
        styles={{ container: { backgroundColor: "rgba(0, 0, 0, .8)", backdropFilter: "blur(10px)" } }}
      />

      {/* Processing Alert */}
      {cruise.state === 'processing' && (
        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200 rounded-md flex items-center gap-3">
          <Spinner size="sm" />
          <span>
            {t('cruiseView.processing') || 'This cruise is currently being processed (calculating route regions). Please refresh the page in a few moments.'}
          </span>
        </div>
      )}

      {/* GLASSMORPHISM HEADER: Breadcrumbs, Actions, Title, Organizer */}
      <div className="mb-8 bg-white/60 dark:bg-slate-900/60 backdrop-blur-md border border-white/20 dark:border-slate-800/50 rounded-2xl shadow-sm pt-4 landscape:py-2">
        {/* Header with Actions */}
        <div className="px-6 mb-6 flex justify-end items-center">
          {(canManage || showRecalculateButton) && (
            <div className="flex flex-wrap gap-2">
              {showRecalculateButton && (
                <button
                  type="button"
                  onClick={handleRecalculate}
                  disabled={cruise.state === 'processing' || recalculateLoading}
                  className="px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-md text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {recalculateLoading ? (
                    <span className="inline-block w-4 h-4 border-2 border-slate-500 border-t-transparent rounded-full animate-spin" aria-hidden />
                  ) : null}
                  {t('adminAllApprovals.actions.recalculate') || t('adminApprovals.actions.recalculate') || 'Recalculate'}
                </button>
              )}
              {canManage && (
                <>
                  <button
                    onClick={handleUnpublishOrDelete}
                    disabled={cruise.state === 'processing'}
                    className={`px-4 py-2 border rounded-md transition-colors ${
                      cruise.state === 'published'
                        ? 'border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-400 dark:hover:bg-amber-900/20' 
                        : 'border-red-300 text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/20'
                    }`}
                  >
                    {cruise.state === 'published' ? (t('dashboardPage.unpublishCruise.button') || 'Unpublish') : (t('common.delete') || 'Remove')}
                  </button>
                  <button
                    onClick={handleEdit}
                    disabled={cruise.state === 'processing'}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors shadow-sm"
                  >
                    {t('cruiseView.actions.edit') || 'Edit'}
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        <div className="px-6 pb-6">
          {/* 2-column header: main (title + organizer) + right (eye + share) */}
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            <div className="flex-1 min-w-0">
              {/* Breadcrumbs (moved into the title block) */}
              <nav aria-label="Breadcrumb" className="mb-3 text-xs md:text-sm text-slate-500 dark:text-slate-400">
                <ol className="flex items-center flex-wrap gap-2">
                  <li>
                    <Link href="/" className="hover:text-blue-600 dark:hover:text-blue-400">
                      {t('common.home') || 'Home'}
                    </Link>
                  </li>
                  <li>
                    <span className="text-slate-300 dark:text-slate-600">/</span>
                  </li>
                  <li>
                    <Link href="/cruises" className="hover:text-blue-600 dark:hover:text-blue-400">
                      {t('cruisesPage.title') || 'Cruises'}
                    </Link>
                  </li>
                  <li>
                    <span className="text-slate-300 dark:text-slate-600">/</span>
                  </li>
                  <li aria-current="page" className="font-medium text-slate-900 dark:text-slate-100 line-clamp-1">
                    {fullTitle}
                  </li>
                </ol>
              </nav>

              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-slate-100">
                {fullTitle}
              </h1>

              {/* Organizer Logo and Name */}
              {cruise.organization_name && (
                <div className="w-full mt-4">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    {cruise.organization_logo_url && (
                      <Image
                        src={cruise.organization_logo_url}
                        alt={cruise.organization_name || 'Organization logo'}
                        width={48}
                        height={48}
                        className="object-contain"
                        unoptimized
                      />
                    )}
                    <div>
                      <h2 className="text-xl font-semibold text-slate-700 dark:text-slate-300">
                        {cruise.organization_name}
                      </h2>
                      {cruise.organization_slug && (
                        <Link
                          // @ts-expect-error - Dynamic route
                          href={`/${cruise.organization_slug}`}
                          className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 inline-block mt-1"
                        >
                          {t('cruiseView.visitOrganizerPage') || 'Visit Organizer Page'}
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col items-end gap-3 md:mt-1 md:pl-4">
              <UniqueVisitsIndicator variant="inline" uniqueVisits={cruise.unique_visits} />
              <ShareCruiseSection title={fullTitle} variant="header" />
            </div>
          </div>
        </div>
      </div>

      {/* MAIN SECTION: Map and Quick Info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12 items-stretch">
        {/* LEFT: Map (65% - 2 columns out of 3) */}
        <div className="lg:col-span-2">
          {cruise.embarkation_place && cruise.disembarkation_place ? (
            <div className="w-full h-[400px] lg:h-full bg-slate-100 dark:bg-slate-800 rounded-2xl overflow-hidden shadow-sm border border-slate-200 dark:border-slate-700">
              <ReadOnlyMapComponent
                embarkationPlace={{
                  id: cruise.embarkation_place.id || '',
                  name: cruise.embarkation_place.name || '',
                  place_name: cruise.embarkation_place.place_name || cruise.embarkation_place.name || '',
                  coordinates: cruise.embarkation_place.coordinates,
                  center: cruise.embarkation_place.center,
                  latitude: cruise.embarkation_place.latitude,
                  longitude: cruise.embarkation_place.longitude,
                  country_code: cruise.embarkation_place.country_code,
                }}
                disembarkationPlace={{
                  id: cruise.disembarkation_place.id || '',
                  name: cruise.disembarkation_place.name || '',
                  place_name: cruise.disembarkation_place.place_name || cruise.disembarkation_place.name || '',
                  coordinates: cruise.disembarkation_place.coordinates,
                  center: cruise.disembarkation_place.center,
                  latitude: cruise.disembarkation_place.latitude,
                  longitude: cruise.disembarkation_place.longitude,
                  country_code: cruise.disembarkation_place.country_code,
                }}
                routeGeoJSON={finalRouteGeoJSON}
                middleBreakPorts={middleBreakPorts}
                routeWaypoints={
                  !Array.isArray(cruise.route) && cruise.route?.waypoints?.length
                    ? cruise.route.waypoints
                    : cruise.route_waypoints ||
                      (Array.isArray(cruise.route)
                        ? cruise.route
                            .slice(1, -1)
                            .map(([lng, lat]) => ({ lat, lng }))
                        : undefined)
                }
                disableInteractions={true}
                enableZoomControl={true}
                className="h-full"
              />
            </div>
          ) : (
            <div className="w-full h-[400px] lg:h-full bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center border border-slate-200 dark:border-slate-700">
              <span className="text-slate-500 dark:text-slate-400">Map not available</span>
            </div>
          )}
        </div>

        {/* RIGHT: Photo & Quick Info Card (35% - 1 column out of 3) */}
        <div className="lg:col-span-1 flex flex-col gap-6">
          {/* MAIN PHOTO: Top of right column */}
          {sortedImages.length > 0 && (
            <div 
              className="w-full aspect-video relative rounded-2xl overflow-hidden shadow-sm border border-slate-200 dark:border-slate-700 cursor-pointer hover:opacity-95 transition-opacity"
              onClick={() => setLightboxIndex(0)}
            >
              <Image
                src={cruise.hero_image_url || sortedImages[0].url || ''}
                alt={`${fullTitle} - Main Image`}
                fill
                className="object-cover"
                priority
                unoptimized
              />
            </div>
          )}

          {/* SUMMARY CARD */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-bold mb-6 text-slate-900 dark:text-slate-100">
              {t('cruiseView.summaryTitle') || 'Quick Info'}
            </h2>

            {(() => {
              // Check if there's any summary data to display
              const hasSummaryData = 
                cruise.price ||
                cruise.embarkation_date ||
                cruise.disembarkation_date ||
                (cruise.total_spots && cruise.total_spots > 0) ||
                plannedDistance ||
                cruise.cruise_time_days ||
                cruise.cruise_type ||
                (cruise.regions && cruise.regions.length > 0) ||
                (cruise.region_names && cruise.region_names.length > 0) ||
                cruise.embarkation_place ||
                cruise.disembarkation_place;

              if (!hasSummaryData) {
                return (
                  <div className="text-slate-500 dark:text-slate-400 italic">
                    {t('cruiseView.summaryNotAvailable') || 'Summary not available'}
                  </div>
                );
              }

              return (
                <div className="flex flex-col gap-6">
                  {/* Price */}
                  {cruise.price && (
                    <div>
                      <span className="block text-sm text-slate-500 dark:text-slate-400 mb-1">
                        {t('cruiseView.price') || 'Price'}
                      </span>
                      <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                        {cruise.price} {cruise.currency || 'EUR'}
                      </div>
                    </div>
                  )}

                  {/* Dates */}
                  {(cruise.embarkation_date || cruise.disembarkation_date) && (
                    <div>
                      <span className="block text-sm text-slate-500 dark:text-slate-400 mb-1">
                        {t('cruiseView.summary.cruiseTime') || 'Dates'}
                      </span>
                      <div className="text-lg font-medium text-slate-900 dark:text-slate-100">
                        {formatDateRange(cruise.embarkation_date, cruise.disembarkation_date)}
                      </div>
                    </div>
                  )}

                  {/* Total Spots */}
                  {cruise.total_spots && cruise.total_spots > 0 && (
                    <div>
                      <span className="block text-sm text-slate-500 dark:text-slate-400 mb-1">
                        {t('createCruise.totalSpots') || 'Total Spots'}
                      </span>
                      <div className="text-lg font-medium text-slate-900 dark:text-slate-100">
                        {cruise.total_spots}
                      </div>
                    </div>
                  )}

                  {/* Distance */}
                  {plannedDistance && (
                    <div>
                      <span className="block text-sm text-slate-500 dark:text-slate-400 mb-1">
                        {t('cruiseView.summary.routeLength') || 'Distance'}
                      </span>
                      <div className="text-lg font-medium text-slate-900 dark:text-slate-100">
                        {Math.round(plannedDistance)} {t('cruiseView.summary.nm') || 'Nm'}
                      </div>
                    </div>
                  )}

                  {/* Duration */}
                  {cruise.cruise_time_days && (
                    <div>
                      <span className="block text-sm text-slate-500 dark:text-slate-400 mb-1">
                        {t('cruiseView.duration') || 'Duration'}
                      </span>
                      <div className="text-lg font-medium text-slate-900 dark:text-slate-100">
                        {t('cruiseView.days', { count: cruise.cruise_time_days })}
                      </div>
                    </div>
                  )}

                  {/* Cruise Type */}
                  {cruise.cruise_type && (
                    <div>
                      <span className="block text-sm text-slate-500 dark:text-slate-400 mb-1">
                        {t('cruiseView.cruiseType') || 'Cruise type'}
                      </span>
                      <div className="text-lg font-medium text-slate-900 dark:text-slate-100">
                        {t(`cruiseType.${cruise.cruise_type}`) || cruise.cruise_type}
                      </div>
                    </div>
                  )}

                  {/* Regions – label-style pills (links when slug available), or processing message */}
                  {(cruise.regions?.length ?? 0) > 0 ? (
                    <div>
                      <span className="block text-sm text-slate-500 dark:text-slate-400 mb-2">
                        {t('cruiseView.regionsTitle') || 'Regions'}
                      </span>
                      <div className="flex flex-wrap gap-2">
                        {cruise.regions!.map((region) => (
                          <Link
                            key={region.slug}
                            href={{ pathname: '/regions/[slug]', params: { slug: region.slug } }}
                            className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 border border-blue-200 dark:border-blue-700 hover:bg-blue-100 dark:hover:bg-blue-800/50 transition-colors"
                          >
                            {region.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : (cruise.region_names?.length ?? 0) > 0 ? (
                    <div>
                      <span className="block text-sm text-slate-500 dark:text-slate-400 mb-2">
                        {t('cruiseView.regionsTitle') || 'Regions'}
                      </span>
                      <div className="flex flex-wrap gap-2">
                        {cruise.region_names!.map((name) => (
                          <span
                            key={name}
                            className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-slate-100 text-slate-700 dark:bg-slate-700/50 dark:text-slate-300 border border-slate-200 dark:border-slate-600"
                          >
                            {name}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : cruise.route_status === 'processing' || cruise.state === 'processing' ? (
                    <div>
                      <span className="block text-sm text-slate-500 dark:text-slate-400 mb-1">
                        {t('cruiseView.regionsTitle') || 'Regions'}
                      </span>
                      <div className="text-lg font-medium text-slate-600 dark:text-slate-300 flex items-center gap-2">
                        <span className="inline-block w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" aria-hidden />
                        {t('cruiseView.regionsProcessing') || 'Regions are being calculated...'}
                      </div>
                    </div>
                  ) : null}

                  {(cruise.embarkation_place || cruise.disembarkation_place || middleBreakPorts.length > 0) && (
                    <div>
                      <span className="block text-sm text-slate-500 dark:text-slate-400 mb-1">
                        {t('cruiseView.route') || 'Route'}
                      </span>
                      <div className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        {[
                          cruise.embarkation_place ? formatPlace(cruise.embarkation_place) : null,
                          ...middleBreakPorts.map((port) => formatPlace(port)),
                          cruise.disembarkation_place ? formatPlace(cruise.disembarkation_place) : null,
                        ]
                          .filter(Boolean)
                          .join(' → ')}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* DESCRIPTION: Wrapped in styled container with Markdown support */}
      {cruise.description && cruise.description.trim() && (
        <div className="mb-12 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm p-6">
          <MarkdownView content={cruise.description} size="lg" />
        </div>
      )}

      {/* ADDITIONAL EQUIPMENT */}
      {cruise.equipment && (() => {
        const equipmentItems = [
          { key: 'webasto', label: t('cruiseWizard.equipment.webastoWithHeating') || 'Webasto' },
          { key: 'spinnaker', label: t('cruiseWizard.equipment.spinnaker') || 'Spinnaker' },
          { key: 'solar_panels', label: t('cruiseWizard.equipment.solarPanels') || 'Solar Panels' },
          { key: 'dinghy_engine', label: t('cruiseWizard.equipment.dinghyEngine') || 'Dinghy Engine' },
          { key: 'wifi', label: t('cruiseWizard.equipment.wifi') || 'WiFi' },
          { key: 'air_conditioning', label: t('cruiseWizard.equipment.airConditioning') || 'Air Conditioning' },
        ].filter((item) => cruise.equipment?.[item.key as keyof typeof cruise.equipment] === true);

        return equipmentItems.length > 0 ? (
          <div className="mb-12 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 space-y-3">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
              {t('cruiseWizard.additionalEquipment') || 'Wyposażenie'}
            </h2>
            <div className="flex flex-wrap gap-2">
              {equipmentItems.map((item) => (
                <span
                  key={item.key}
                  className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200 px-3 py-1 text-xs font-semibold"
                >
                  ✓ {item.label}
                </span>
              ))}
            </div>
          </div>
        ) : null;
      })()}

      {/* GALLERY: Below description, aspect-video */}
      {sortedImages.length > 1 && (
        <div className="mb-12 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm p-6">
          <h2 className="text-2xl font-bold mb-6 text-slate-900 dark:text-slate-100">
            {t('cruiseView.imagesGallery') || 'Gallery'}
          </h2>
          <div className="p-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {sortedImages
                .slice(1)
                .map((image, index) => (
                  <div 
                    key={image.id || index} 
                    className="relative aspect-video overflow-hidden rounded-xl group cursor-pointer hover:opacity-90 transition-all"
                    onClick={() => setLightboxIndex(index + 1)}
                  >
                    <Image
                      src={image.url || ''}
                      alt={`${fullTitle} - Image ${index + 2}`}
                      fill
                      className="object-cover rounded-xl transition-transform duration-300 group-hover:scale-105"
                      unoptimized
                    />
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Share Cruise Section */}
      {/* moved to header */}

      {/* CTA Contact Form - Always visible, no login required, Cloudflare protected */}
      <CTAForm
        cruiseId={cruise.main_cruise_id || cruiseId}
        recipientName={cruise.organization_name || cruise.name || fullTitle}
        recipientType="cruise"
      />

      {/* Unpublish/Delete Confirmation Dialog */}
      {deleteDialogOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-xl max-w-md w-full overflow-hidden border border-slate-200 dark:border-slate-800">
            <div className="p-6">
              <h3 className="text-xl font-bold mb-3 text-slate-900 dark:text-slate-100">
                {cruise?.state === 'published'
                  ? t('dashboardPage.unpublishCruise.title') || 'Unpublish Cruise'
                  : t('dashboardPage.deleteCruise.title') || 'Delete Cruise'}
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-6">
                {cruise?.state === 'published'
                  ? t('dashboardPage.unpublishCruise.message', { name: cruise?.name || '' }) ||
                    `Are you sure you want to unpublish "${cruise?.name}"? It will revert to draft status.`
                  : t('dashboardPage.deleteCruise.message', { name: cruise?.name || '' }) ||
                    `Are you sure you want to delete "${cruise?.name}"? This action cannot be undone.`}
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={handleCancelUnpublishOrDelete}
                  disabled={deleting}
                  className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-md transition-colors"
                >
                  {t('common.cancel') || 'Cancel'}
                </button>
                <button
                  onClick={handleConfirmUnpublishOrDelete}
                  disabled={deleting}
                  className={`px-4 py-2 text-white rounded-md transition-colors flex items-center gap-2 ${
                    cruise?.state === 'published'
                      ? 'bg-amber-500 hover:bg-amber-600'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {deleting && <Spinner size="sm" />}
                  {deleting ? (
                    'Processing...'
                  ) : cruise?.state === 'published' ? (
                    t('dashboardPage.unpublishCruise.button') || 'Unpublish'
                  ) : (
                    t('common.delete') || 'Delete'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

